import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Header />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-foreground-700 mb-4">未找到该漫画</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbs = [
    { label: '首页', path: '/' },
    { label: comic.category, path: `/category/${comic.categorySlug}` },
    { label: comic.title, path: `/comic/${comic.slug}` },
  ];

  // Related comics: same category, excluding current
  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 在线免费阅读 - 073漫画网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    dateModified: comic.updateTime,
    url: `http://dqcmwfc.com/comic/${comic.slug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((bc, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: { '@id': `http://dqcmwfc.com/${bc.path}`, name: bc.label },
      })),
    },
  };

  return (
    <>
      <Header />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />

        <nav className="max-w-[1280px] mx-auto px-6 md:px-10 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            {breadcrumbs.map((bc, i) => (
              <li key={bc.path} className="flex items-center gap-2">
                {i > 0 && <span className="text-foreground-300">/</span>}
                {i < breadcrumbs.length - 1 ? (
                  <Link to={bc.path} className="hover:text-primary-600 transition-colors">
                    {bc.label}
                  </Link>
                ) : (
                  <span className="text-foreground-700 font-medium">{bc.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <article className="max-w-[1280px] mx-auto px-6 md:px-10 py-6">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0 w-full md:w-72">
              <div className="rounded-lg overflow-hidden aspect-[3/4] shadow-sm">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 在线免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-primary-100 text-primary-700">
                  {comic.status}
                </span>
                <span className="text-sm text-accent-600 flex items-center gap-1">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                  {comic.rating}
                </span>
                <span className="text-sm text-foreground-500">
                  {comic.views.toLocaleString()} 次阅读
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${comic.categorySlug}`}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-4 mb-6">
                <h2 className="text-sm font-heading font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/comic/${comic.slug}/chapter/1`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  开始阅读
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 border border-background-200 hover:bg-background-200 text-foreground-700 font-medium text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-line w-4 h-4 flex items-center justify-center"></i>
                  更多{comic.category}漫画
                </Link>
              </div>
            </div>
          </div>

          <section className="mt-10">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {comic.chapters.length} 话
              </span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {[...comic.chapters].reverse().map((ch) => (
                <Link
                  key={ch.num}
                  to={`/comic/${comic.slug}/chapter/${ch.num}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-background-50 transition-all duration-200"
                >
                  <span className="text-sm text-foreground-800 truncate">
                    第{ch.num}话：{ch.title}
                  </span>
                  <span className="text-xs text-foreground-500 flex-shrink-0 ml-2">
                    {ch.updateTime}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-12">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.slug}`}
                    className="group flex flex-col cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 在线免费阅读`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-medium bg-primary-500 text-background-50">
                        {rc.status}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <span className="text-xs text-accent-600 flex items-center gap-0.5 mt-0.5">
                      <i className="ri-star-fill w-3 h-3 flex items-center justify-center"></i>
                      {rc.rating}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}